/**
 * Client for POST /api/cognitory/v2/provisioning-setup/activation-readiness (read-only).
 */

import {
  COGNITORY_V2_PROVISIONING_PRODUCT_ACTIVATION_READINESS_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import { parseNonemptyRequestId } from "./cognitoryProvisioningLibraryStateClient";

export const PROVISIONING_PRODUCT_ACTIVATION_NOT_READY_REASONS = [
  "system_not_ready",
  "candidate_not_available",
  "candidate_not_ready",
  "candidate_identity_invalid",
  "candidate_metadata_invalid",
  "activation_pointer_present",
] as const;

export type ProvisioningProductActivationNotReadyReason =
  (typeof PROVISIONING_PRODUCT_ACTIVATION_NOT_READY_REASONS)[number];

export type ProvisioningProductActivationReadinessClientResult =
  | { kind: "ready"; requestId: string }
  | {
      kind: "not_ready";
      requestId: string;
      reason: ProvisioningProductActivationNotReadyReason;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "system_not_eligible"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

function hasExactKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const keys = Object.keys(record);
  if (keys.length !== allowed.length) {
    return false;
  }
  for (const key of allowed) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      return false;
    }
  }
  return true;
}

function isNotReadyReason(
  value: unknown,
): value is ProvisioningProductActivationNotReadyReason {
  return (
    typeof value === "string" &&
    (
      PROVISIONING_PRODUCT_ACTIVATION_NOT_READY_REASONS as readonly string[]
    ).includes(value)
  );
}

type ReadinessDenialKind =
  | "unauthenticated"
  | "forbidden"
  | "invalid_request"
  | "system_not_eligible"
  | "temporary_failure";

function parseDenial(
  status: number,
  body: unknown,
  expectedDecision: ReadinessDenialKind,
  expectedStatus: number,
): { kind: ReadinessDenialKind; requestId: string } | null {
  if (status !== expectedStatus || !body || typeof body !== "object") {
    return null;
  }
  const record = body as Record<string, unknown>;
  if (!hasExactKeys(record, ["decision", "requestId"])) {
    return null;
  }
  if (record.decision !== expectedDecision) {
    return null;
  }
  const requestId = parseNonemptyRequestId(record.requestId);
  if (!requestId) {
    return null;
  }
  return { kind: expectedDecision, requestId };
}

async function postProvisioningProductActivationReadiness(
  cognitoryId: string,
  input?: {
    apiBaseUrl?: string;
    bearerToken?: string | null;
    fetchImpl?: typeof fetch;
  },
): Promise<{ status: number; body: unknown }> {
  const fetchImpl = input?.fetchImpl ?? fetch;
  const token =
    input?.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { status: 401, body: null };
  }

  const base = input?.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  try {
    const response = await fetchImpl(
      `${base}${COGNITORY_V2_PROVISIONING_PRODUCT_ACTIVATION_READINESS_PATH}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        cache: "no-store",
        body: JSON.stringify({ cognitoryId }),
      },
    );
    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
    return { status: response.status, body: parsed };
  } catch {
    return { status: 503, body: null };
  }
}

export async function fetchProvisioningProductActivationReadiness(input: {
  cognitoryId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<ProvisioningProductActivationReadinessClientResult> {
  const { status, body } = await postProvisioningProductActivationReadiness(
    input.cognitoryId,
    input,
  );

  if (status === 401) {
    const denial = parseDenial(status, body, "unauthenticated", 401);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 403) {
    const denial = parseDenial(status, body, "forbidden", 403);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 400) {
    const invalid = parseDenial(status, body, "invalid_request", 400);
    if (invalid) {
      return invalid;
    }
    const ineligible = parseDenial(status, body, "system_not_eligible", 400);
    if (ineligible) {
      return ineligible;
    }
    return { kind: "unable_to_verify", requestId: null };
  }
  if (status === 503) {
    const denial = parseDenial(status, body, "temporary_failure", 503);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 200) {
    if (!body || typeof body !== "object") {
      return { kind: "unable_to_verify", requestId: null };
    }
    const record = body as Record<string, unknown>;
    const requestId = parseNonemptyRequestId(record.requestId);
    if (!requestId) {
      return { kind: "unable_to_verify", requestId: null };
    }
    if (
      hasExactKeys(record, ["decision", "requestId", "mode"]) &&
      record.decision === "product_activation_ready" &&
      record.mode === "read_only"
    ) {
      return { kind: "ready", requestId };
    }
    if (
      hasExactKeys(record, ["decision", "requestId", "mode", "reason"]) &&
      record.decision === "product_activation_not_ready" &&
      record.mode === "read_only" &&
      isNotReadyReason(record.reason)
    ) {
      return {
        kind: "not_ready",
        requestId,
        reason: record.reason,
      };
    }
    return { kind: "unable_to_verify", requestId: null };
  }

  return { kind: "unable_to_verify", requestId: null };
}

export function provisioningProductActivationNotReadyMessage(
  reason: ProvisioningProductActivationNotReadyReason,
): string {
  switch (reason) {
    case "system_not_ready":
      return "This Cognitory is not ready for activation yet.";
    case "candidate_not_available":
      return "The activation candidate is not available yet.";
    case "candidate_not_ready":
      return "The library build is not activation-ready yet.";
    case "candidate_identity_invalid":
      return "Activation readiness could not be verified.";
    case "candidate_metadata_invalid":
      return "Activation readiness metadata could not be verified.";
    case "activation_pointer_present":
      return "This Cognitory already has active serving pointers.";
    default:
      return "Activation readiness could not be verified.";
  }
}
